<script setup>
import { ref } from 'vue'
import { Head, Link, useForm, router } from '@inertiajs/vue3'
import { ArrowLeft, Plane, Plus, KeyRound, FileText } from 'lucide-vue-next'
import AdminLayout from '../../Layout/AdminLayout.vue'
import Field from '../../Components/Admin/Field.vue'

defineOptions({ layout: AdminLayout })

const props = defineProps({
  client: { type: Object, required: true },
  aircraft: { type: Array, required: true },
})

const dados = useForm({
  client: { name: props.client.name, email: props.client.email, phone: props.client.phone ?? '' },
})
const senha = useForm({ password: '' })
const nova = useForm({ aircraft: { registration: '', model: '' } })
const showAircraft = ref(false)

const salvar = () => dados.patch(`/admin/clientes/${props.client.id}`)
const redefinir = () =>
  senha.patch(`/admin/clientes/${props.client.id}/senha`, { onSuccess: () => senha.reset() })
const criarAeronave = () =>
  nova.post(`/admin/clientes/${props.client.id}/aeronaves`, {
    onSuccess: () => {
      nova.reset()
      showAircraft.value = false
    },
  })

const remover = () => {
  // destroy leva junto aeronaves, ordens de serviço e arquivos: vale confirmar.
  if (confirm(`Remover ${props.client.name} e tudo ligado a ele? Isso não pode ser desfeito.`)) {
    router.delete(`/admin/clientes/${props.client.id}`)
  }
}
</script>

<template>
  <Head :title="`${props.client.name} · Administração`" />

  <Link href="/admin" class="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-up-navy">
    <ArrowLeft class="h-4 w-4" aria-hidden="true" />
    Clientes
  </Link>

  <h1 class="mt-3 text-xl font-semibold tracking-tight text-up-navy">{{ props.client.name }}</h1>

  <div class="mt-6 grid gap-6 lg:grid-cols-3">
    <div class="space-y-6 lg:col-span-2">
      <form class="rounded-card bg-white p-6 shadow-card" @submit.prevent="salvar">
        <h2 class="text-sm font-semibold tracking-wide text-up-navy uppercase">Dados</h2>
        <div class="mt-5 grid gap-4 sm:grid-cols-2">
          <Field id="name" v-model="dados.client.name" label="Nome" required :error="dados.errors.name" />
          <Field id="email" v-model="dados.client.email" label="E-mail" type="email" required :error="dados.errors.email" />
          <Field id="phone" v-model="dados.client.phone" label="Telefone" :error="dados.errors.phone" />
        </div>
        <button
          type="submit"
          :disabled="dados.processing"
          class="mt-5 rounded-lg bg-up-orange px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-up-orange-dark disabled:opacity-60"
        >
          {{ dados.processing ? 'Salvando…' : 'Salvar' }}
        </button>
      </form>

      <section class="rounded-card bg-white p-6 shadow-card">
        <div class="flex flex-wrap items-center gap-4">
          <h2 class="text-sm font-semibold tracking-wide text-up-navy uppercase">Aeronaves</h2>
          <button
            type="button"
            class="ml-auto flex items-center gap-2 rounded-lg border border-up-orange px-3 py-2 text-sm font-semibold text-up-orange transition-colors hover:bg-up-orange/5"
            @click="showAircraft = !showAircraft"
          >
            <Plus class="h-4 w-4" aria-hidden="true" />
            Nova aeronave
          </button>
        </div>

        <form v-if="showAircraft" class="mt-5 grid gap-4 border-t border-slate-100 pt-5 sm:grid-cols-2" @submit.prevent="criarAeronave">
          <Field id="reg" v-model="nova.aircraft.registration" label="Matrícula" required hint="Ex.: PR-ABC" :error="nova.errors.registration" />
          <Field id="mod" v-model="nova.aircraft.model" label="Modelo" required hint="Ex.: TBM 960" :error="nova.errors.model" />
          <div class="sm:col-span-2">
            <button
              type="submit"
              :disabled="nova.processing"
              class="rounded-lg bg-up-orange px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-up-orange-dark disabled:opacity-60"
            >
              {{ nova.processing ? 'Salvando…' : 'Cadastrar aeronave' }}
            </button>
          </div>
        </form>

        <p v-if="!props.aircraft.length" class="mt-5 text-sm text-slate-500">
          Nenhuma aeronave cadastrada. O portal deste cliente ficará sem conteúdo até existir uma.
        </p>

        <ul v-else class="mt-5 divide-y divide-slate-100 overflow-hidden rounded-lg border border-slate-100">
          <li v-for="a in props.aircraft" :key="a.id">
            <Link :href="`/admin/aeronaves/${a.id}`" class="flex items-center gap-3 px-4 py-3.5 transition-colors hover:bg-up-bg">
              <Plane class="h-5 w-5 shrink-0 text-up-navy" aria-hidden="true" />
              <div class="min-w-0">
                <p class="truncate font-medium text-up-navy">{{ a.registration }} · {{ a.model }}</p>
                <p class="mt-0.5 flex items-center gap-2 text-xs text-slate-500">
                  <span v-if="a.serviceOrder">{{ a.serviceOrder }}</span>
                  <span v-else class="text-slate-400">sem ordem de serviço</span>
                  <span class="text-slate-300" aria-hidden="true">·</span>
                  <span class="flex items-center gap-1"><FileText class="h-3.5 w-3.5" aria-hidden="true" />{{ a.documentCount }}</span>
                </p>
              </div>
            </Link>
          </li>
        </ul>
      </section>
    </div>

    <div class="space-y-6">
      <form class="rounded-card bg-white p-6 shadow-card" @submit.prevent="redefinir">
        <h2 class="flex items-center gap-2 text-sm font-semibold tracking-wide text-up-navy uppercase">
          <KeyRound class="h-4 w-4" aria-hidden="true" />
          Redefinir senha
        </h2>
        <!-- É o caminho oficial: o portal não tem "esqueci minha senha". -->
        <p class="mt-1 text-sm text-slate-500">
          O cliente não consegue redefinir sozinho. Escolha a nova senha aqui e envie por um canal combinado.
        </p>
        <div class="mt-4">
          <Field id="pw" v-model="senha.password" label="Nova senha" type="password" required hint="Mínimo de 8 caracteres." :error="senha.errors.password" />
        </div>
        <button
          type="submit"
          :disabled="senha.processing"
          class="mt-4 w-full rounded-lg border border-up-orange px-4 py-2.5 text-sm font-semibold text-up-orange transition-colors hover:bg-up-orange/5 disabled:opacity-60"
        >
          {{ senha.processing ? 'Salvando…' : 'Redefinir' }}
        </button>
      </form>

      <div class="rounded-card bg-white p-6 shadow-card">
        <h2 class="text-sm font-semibold tracking-wide text-up-navy uppercase">Remover cliente</h2>
        <p class="mt-1 text-sm text-slate-500">
          Apaga também aeronaves, ordens de serviço, documentos e arquivos deste cliente.
        </p>
        <button
          type="button"
          class="mt-4 w-full rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:border-up-orange hover:text-up-orange"
          @click="remover"
        >
          Remover
        </button>
      </div>
    </div>
  </div>
</template>
